import React, { useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import type { MediaImage } from './ImagesVideo';
import MainImageLibraryTile from './MainImageLibraryTile';

interface Props {
  hiddenImagePublicIds: Array<string>;
  mediaImages: Array<MediaImage>;
  onDeleteImage: (publicId: string) => void;
  onToggleHiddenImage: (publicId: string) => void;
}

export interface OrderedMediaImage {
  image: MediaImage;
  sortId: string;
}

function buildOrderedImages(images: Array<MediaImage>): Array<OrderedMediaImage> {
  return images.map((image) => ({
    image,
    sortId: image.publicId,
  }));
}

function mergeOrderedImages(
  currentImages: Array<OrderedMediaImage>,
  nextImages: Array<MediaImage>,
): Array<OrderedMediaImage> {
  const nextImagesById = new Map(nextImages.map((image) => [image.publicId, image]));
  const existingImages = currentImages
    .filter((orderedImage) => nextImagesById.has(orderedImage.sortId))
    .map((orderedImage) => ({
      ...orderedImage,
      image: nextImagesById.get(orderedImage.sortId) ?? orderedImage.image,
    }));
  const existingImageIds = new Set(existingImages.map((image) => image.sortId));
  const newImages = nextImages
    .filter((image) => !existingImageIds.has(image.publicId))
    .map((image) => ({
      image,
      sortId: image.publicId,
    }));

  return existingImages.concat(newImages);
}

export default function MainImageLibrary({
  hiddenImagePublicIds,
  mediaImages,
  onDeleteImage,
  onToggleHiddenImage,
}: Props) {
  const [orderedImages, setOrderedImages] = useState<Array<OrderedMediaImage>>(() => buildOrderedImages(mediaImages));
  const [imagePendingDeletion, setImagePendingDeletion] = useState<MediaImage | undefined>();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setOrderedImages((currentImages) => mergeOrderedImages(currentImages, mediaImages));
  }, [mediaImages]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setOrderedImages((currentImages) => {
      const oldIndex = currentImages.findIndex((image) => image.sortId === active.id);
      const newIndex = currentImages.findIndex((image) => image.sortId === over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return currentImages;
      }

      return arrayMove(currentImages, oldIndex, newIndex);
    });
  };

  const handleCloseDeleteDialog = () => {
    setImagePendingDeletion(undefined);
  };

  const handleConfirmDelete = () => {
    if (!imagePendingDeletion) {
      return;
    }

    onDeleteImage(imagePendingDeletion.publicId);
    setImagePendingDeletion(undefined);
  };

  return (
    <>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ px: 3, py: 2.5 }}>
          <Typography variant="h5">Main image library</Typography>
          <Chip
            label={`${mediaImages.length} ${mediaImages.length === 1 ? 'image' : 'images'}`}
            color="info"
            variant="outlined"
          />
        </Stack>
        <Divider />

        {orderedImages.length > 0 ? (
          <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
            <SortableContext items={orderedImages.map((image) => image.sortId)} strategy={rectSortingStrategy}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 2,
                  p: 3,
                }}
              >
                {orderedImages.map((orderedImage) => (
                  <MainImageLibraryTile
                    key={orderedImage.sortId}
                    isHidden={hiddenImagePublicIds.includes(orderedImage.image.publicId)}
                    orderedImage={orderedImage}
                    onDeleteImage={setImagePendingDeletion}
                    onToggleHiddenImage={onToggleHiddenImage}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No images have been added to this offer yet.
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog open={!!imagePendingDeletion} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete image?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the image from the main image library and from any room image slots.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseDeleteDialog}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
